import type { TablePagination } from '@lifebit-ai/lds'
import { useCallback, useMemo, useState } from 'react'

import { useToggleState } from 'hooks/useToggleState'
import {
  type ImportRequestsTableFiltersState,
  buildImportRequestsVerificationParams,
  defaultImportRequestsTableFiltersState,
  useImportRequestsTable
} from 'modules/requests/hooks/useImportRequestsTable'
import type { Option } from 'modules/types'
import { useBulkUnassignImportRequests } from 'services/import-requests/hooks/useBulkUnassignImportRequests'
import { DEFAULT_PAGE_SIZE } from 'services/results'

const countActiveImportRequestFilters = (filtersState: ImportRequestsTableFiltersState): number =>
  Object.values(filtersState).filter(value =>
    Array.isArray(value) ? value.length > 0 : typeof value === 'string' && value.length > 0
  ).length

export const useImportRequestsTablePage = () => {
  const [tableState, setTableState] = useState<{ page: number; pageSize: number }>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE
  })
  const [filtersState, setFiltersState] = useState(defaultImportRequestsTableFiltersState)
  const [areFiltersVisible, toggleFiltersVisibility] = useToggleState(true)
  const [assignModalState, setAssignModalState] = useState<{ requestId: string; assignedTo?: string } | null>(null)

  const queryParams = useMemo(
    () => buildImportRequestsVerificationParams(tableState, filtersState),
    [filtersState, tableState]
  )

  const { columns, importRequests, isLoading, openImportRequest, pagination, showPagination } =
    useImportRequestsTable(queryParams)

  const bulkUnassignMutation = useBulkUnassignImportRequests()

  const openAssignModal = useCallback((requestId: string, assignedTo?: string) => {
    setAssignModalState({ requestId, assignedTo })
  }, [])

  const closeAssignModal = useCallback(() => {
    setAssignModalState(null)
  }, [])

  const unassignImportRequest = useCallback(
    (requestId: string) => {
      bulkUnassignMutation.mutate({ requestIds: [requestId] })
    },
    [bulkUnassignMutation]
  )

  const activeFiltersCount = useMemo(() => countActiveImportRequestFilters(filtersState), [filtersState])

  const resetPage = useCallback(() => {
    setTableState(prev => ({ ...prev, page: 0 }))
  }, [])

  const applyCheckboxFilter = useCallback(
    (
        filterKey: keyof Pick<
          ImportRequestsTableFiltersState,
          'status' | 'scanResult' | 'labelIds' | 'workspaceIds' | 'requesterIds' | 'assigneeIds'
        >
      ) =>
      (filteredOptions: Option[]) => {
        setFiltersState(prev => ({
          ...prev,
          [filterKey]: filteredOptions.map(option => option.value)
        }))
        resetPage()
      },
    [resetPage]
  )

  const resetCheckboxFilter = useCallback(
    (
        filterKey: keyof Pick<
          ImportRequestsTableFiltersState,
          'status' | 'scanResult' | 'labelIds' | 'workspaceIds' | 'requesterIds' | 'assigneeIds'
        >
      ) =>
      () => {
        setFiltersState(prev => ({ ...prev, [filterKey]: [] }))
        resetPage()
      },
    [resetPage]
  )

  const applyFileNameFilter = useCallback(
    (searchTerm: string) => {
      setFiltersState(prev => ({ ...prev, fileName: searchTerm.trim() }))
      resetPage()
    },
    [resetPage]
  )

  const resetFileNameFilter = useCallback(() => {
    setFiltersState(prev => ({ ...prev, fileName: '' }))
    resetPage()
  }, [resetPage])

  const tablePagination: TablePagination = useMemo(
    () => ({
      ...pagination,
      onPageChange: (page: number) => {
        setTableState(prev => ({ ...prev, page }))
      },
      onPageSizeChange: (pageSize: number) => {
        setTableState(prev => ({ ...prev, page: 0, pageSize }))
      }
    }),
    [pagination]
  )

  return {
    activeFiltersCount,
    applyCheckboxFilter,
    applyFileNameFilter,
    areFiltersVisible,
    assignModalState,
    closeAssignModal,
    columns,
    importRequests,
    isLoading,
    openAssignModal,
    openImportRequest,
    resetCheckboxFilter,
    resetFileNameFilter,
    showPagination,
    tablePagination,
    toggleFiltersVisibility,
    unassignImportRequest
  }
}
